/**
 * Voice resource — per-org Twilio voice config + call history (ARCHITECTURE §H).
 */

import type { HttpClient } from '../httpClient';
import { endpoints } from '../endpoints';
import type {
  CallLog,
  VoiceConfig,
  VoiceConfigInput,
  VoiceNumber,
  VoiceNumberInput,
  VoiceNumberUpdate,
  VoiceToken,
} from '../../types';

export class VoiceResource {
  constructor(private readonly http: HttpClient) {}

  config(): Promise<VoiceConfig> {
    return this.http.get(endpoints.voice.config());
  }

  /** Replace the org's voice config (full PUT). `authToken` is write-only; omit to keep it. */
  updateConfig(input: VoiceConfigInput): Promise<VoiceConfig> {
    return this.http.put(endpoints.voice.config(), input);
  }

  calls(): Promise<CallLog[]> {
    return this.http.get(endpoints.voice.calls());
  }

  /** Whether the org's softphone is fully configured (any agent may call this). */
  available(): Promise<{ softphoneReady: boolean; hasNumber?: boolean }> {
    return this.http.get(endpoints.voice.available());
  }

  /** Mint a short-lived access token for the current agent's browser softphone. */
  token(): Promise<VoiceToken> {
    return this.http.post(endpoints.voice.token(), {});
  }

  /** The org's Twilio number pool (each optionally assigned to an agent). */
  numbers(): Promise<VoiceNumber[]> {
    return this.http.get(endpoints.voice.numbers());
  }

  createNumber(input: VoiceNumberInput): Promise<VoiceNumber> {
    return this.http.post(endpoints.voice.numbers(), input);
  }

  /** Assign / relabel / toggle a number (only the fields sent are applied). */
  updateNumber(id: string, input: VoiceNumberUpdate): Promise<VoiceNumber> {
    return this.http.patch(endpoints.voice.number(id), input);
  }

  deleteNumber(id: string): Promise<void> {
    return this.http.delete(endpoints.voice.number(id));
  }
}
